import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, NavigationStart, RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import {
  AlertModule,
  ButtonModule,
  CardModule,
  GridModule,
  SpinnerModule,
  TableModule
} from '@coreui/angular-pro';
import { IconDirective, IconModule } from '@coreui/icons-angular';
import { UserService, type DashboardStats } from 'app';
import { AuthService } from 'auth';
import { AppointmentService, CardAccountService, NewsletterOptinComponent, UserPoolService, WpcpDashboardWidgetComponent, type AppointmentOccurrence } from 'credo';
import { I18nService, TranslatePipe, TranslateService, TranslationModule } from 'i18n';
import { ImageLink, Media, MediaLink, MediaService, MediaViewerOverlayService, PdfMedia } from 'media';
import { CardAccount, FoundingMemberBadgeComponent, Logger, type User, type UserPool } from 'shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { filter } from 'rxjs';

import {
  FunnelService,
  type BookingTerms,
  type BundleFulfillment,
  type BundleShippingAddress,
  type GuaranteePayoutMethod,
  type MembershipApplication,
  type MembershipCategory,
  type MembershipTier,
  type Memberships,
  type QuizPlan,
  type SpecialUpgradeOffer,
  type UpgradeOffer
} from '../../../services/funnel.service';
import { MarketingPoolCardComponent } from '../../../components/marketing-pool-card/marketing-pool-card.component';
import { environment } from '../../../environments/environment';
import { ALL_BUNDLE_PLAN_CODES } from '../../../services/funnel-intent';
import { countryName, countryOptions } from '../../../services/countries';
import { PRODUCT_PRICES, ProductKey, productLabel } from '../../../services/product-label';

/** Maps a plan code to its `funnel.plans.<key>` i18n node for the display name. */
const PLAN_KEY_BY_CODE: Record<string, string> = {
  GH_CLUB_WEEKLY:         'club_weekly',
  GH_CLUB_YEARLY:         'club_yearly',
  GH_CLUB:                'club',
  GH_CLUB_MONTHLY:        'club_monthly',
  GH_CLUB_SPECIAL:        'club_special',
  GH_PRACTITIONER_WEEKLY: 'practitioner_weekly',
  GH_PRACTITIONER_YEARLY: 'practitioner_yearly',
  GH_DNA_HEALTH:          'dna_health',
  GH_DNA_HEALTH_SPECIAL:  'dna_health_special',
  GH_DNA_FAMILY_SPECIAL:  'dna_family_special',
  GH_SHIELD:              'shield',
  GH_EXECUTIVE:           'executive',
  GH_LEGACY:              'legacy'
};

/** The public website's journeys and bundles a partner can share (Website V2),
 *  in the order the dashboard lists them. `key` is the `home.referral_links.<key>`
 *  label, `path` the page on the website. */
const REFERRAL_TARGETS: ReadonlyArray<{ key: string; path: string; hash?: string }> = [
  { key: 'website',            path: '/' },
  { key: 'weight',             path: '/weight' },
  { key: 'longevity',          path: '/longevity' },
  { key: 'performance',        path: '/performance' },
  { key: 'better_health',      path: '/better-health' },
  { key: 'business',           path: '/business' },
  // The homepage's practitioner block (id="practitioner") is the direct entry to
  // the Practitioner Membership. The fragment goes AFTER the query, or the
  // website's referral engine would never see `?ref=`.
  { key: 'practitioner',       path: '/', hash: 'practitioner' },
  { key: 'dna_health_special', path: '/dna-health-special' }
];

export interface ReferralLink {
  key: string;
  url: string;
}

/** The Practitioner Network plans — the club slot's upper tier. The fallback for a
 *  backend that does not report a plan's `tier` yet. */
const PRACTITIONER_PLAN_CODES = ['GH_PRACTITIONER_WEEKLY', 'GH_PRACTITIONER_YEARLY'];
/** The regular DNA & Health SPECIAL — the one bundle sold to every member. */
const SPECIAL_CODE = 'GH_DNA_HEALTH_SPECIAL';

/** A dashboard membership slot: the category and the user's application in it (if any). */
interface MembershipSlot {
  category: MembershipCategory;
  app:      MembershipApplication | null;
}

/** One product in the initial picker (club tier first, practitioner after); its
 *  plans are the duration options of that one product. */
interface PlanTierGroup {
  tier:    MembershipTier;
  product: ProductKey;
  plans:   QuizPlan[];
}

/** Per-session dismissal of the "please confirm your e-mail" banner. */
const VERIFY_BANNER_DISMISSED_KEY = 'globalhealth.verify_email_banner_dismissed';

@Component({
  selector: 'main-page',
  standalone: true,
  imports: [
    MarketingPoolCardComponent,
    CommonModule,
    FormsModule,
    RouterModule,
    AlertModule,
    ButtonModule,
    CardModule,
    GridModule,
    IconModule,
    SpinnerModule,
    TableModule,
    TranslationModule,
    TranslatePipe,
    IconDirective,
    WpcpDashboardWidgetComponent,
    NewsletterOptinComponent,
    FoundingMemberBadgeComponent,
  ],
  templateUrl: './main-page.component.html',
  styleUrls: ['./main-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MainPageComponent {
  protected logger = new Logger('MainPageComponent');

  private userService = inject(UserService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private userPoolService = inject(UserPoolService);
  private cardAccountService = inject(CardAccountService);
  private mediaService = inject(MediaService);
  private mediaViewer = inject(MediaViewerOverlayService);
  private i18n = inject(I18nService);
  private router = inject(Router);
  private http = inject(HttpClient);
  private funnel = inject(FunnelService);
  private destroyRef = inject(DestroyRef);

  // ────────────────────────────────────────────────
  // State signals
  // ────────────────────────────────────────────────
  user = signal<User | null>(null);
  stats = signal<DashboardStats | null>(null);
  userPools = signal<UserPool[]>([]);
  userCards = signal<CardAccount[]>([]);
  domainId = signal<number | null>(null);
  lic1Active = signal<boolean>(false);

  loading = signal(true);
  poolsLoading = signal(false);
  cardsLoading = signal(false);
  error = signal<string | null>(null);

  // Email stats
  confirmedEmailsCount = signal(0);
  poolsRequiringEmails = signal(0);
  requiredEmailsCount = signal(0);

  // Latest media
  media = signal<Media | null>(null);

  // ── "Health in the Light" (2026-09-22) ──────────────────────────────────────
  // The first screen answers, in the customer's order: Who am I here? What can I
  // do? Where is Dr. Future? Where is my community? Where do I get help?
  private appointments = inject(AppointmentService);

  /** First name for "Welcome back, {{name}}."; falls back to the username. */
  firstName = computed(() => this.user()?.first_name?.trim() || this.user()?.username || '');

  /** The next appointments of the brand, at most three. */
  upcoming = signal<AppointmentOccurrence[]>([]);

  /**
   * The Health Club entries. "Find a Professional" and "Explore Health" are real
   * cards that are honestly marked as coming (customer answers 5/6): the future
   * Practitioner Directory and Health Knowledge areas plug in by switching
   * `enabled` and setting a route — the dashboard needs no redesign for them.
   */
  readonly healthCards: ReadonlyArray<{ key: string; icon: string; route: string[] | null; enabled: boolean }> = [
    { key: 'concierge',    icon: 'cilCompass',      route: ['dr-future'],                     enabled: true },
    { key: 'professional', icon: 'cilMedicalCross', route: null,                               enabled: false },
    { key: 'knowledge',    icon: 'cilDescription',  route: null,                               enabled: false },
    // A Health Club member lands in the Health Club group, not in a group overview.
    { key: 'community',    icon: 'cilPeople',       route: ['community', 'g', 'health_club'], enabled: true },
  ];

  /** Dr. Future hero: the question typed here opens a new Dr. Future conversation and is sent at once. */
  heroQuestion = signal('');

  askDrFuture(): void {
    const q = this.heroQuestion().trim();
    this.router.navigate(['/', this.currentLang(), 'dr-future'], { queryParams: q ? { q } : {} });
  }

  private loadUpcomingAppointments(): void {
    this.appointments.getUpcoming(this.i18n.getLanguage(), 6)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: res => this.upcoming.set((res.appointments ?? []).slice(0, 3)),
          error: () => this.upcoming.set([])
        });
  }

  appointmentDay(a: AppointmentOccurrence): string {
    return new Date(a.starts_at).toLocaleDateString(this.currentLang(), { day: '2-digit' });
  }

  appointmentMonth(a: AppointmentOccurrence): string {
    return new Date(a.starts_at).toLocaleDateString(this.currentLang(), { month: 'short' });
  }

  appointmentWhen(a: AppointmentOccurrence): string {
    return new Date(a.starts_at).toLocaleString(this.currentLang(), { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  }

  // Both Global Health membership slots (club + protection). Each drives its own
  // dashboard card: finalize / bank-transfer / purchased status / "add" prompt.
  memberships = signal<Memberships | null>(null);

  // All five purchasable plans (both club variants + the three protection plans),
  // from the backend. Shown as the initial plan picker before anything is chosen.
  allPlans = signal<QuizPlan[]>([]);

  /**
   * The DNA & Health SPECIAL as the backend lists it (asked for by code, see
   * loadMemberships) — the source of the price shown on the dashboard. Kept apart
   * from `allPlans` so the membership picker and the tier CTAs never see a bundle.
   * Null when the product is not on sale (inactive) or the backend did not list it.
   */
  specialPlan = signal<QuizPlan | null>(null);

  // The `funnel.plans` locale node ({ club: {...}, shield: {...}, … }) — source of
  // each plan card's tagline / price / features (same as the wizard's plan step).
  private planContent = this.translate.translateObjectSignal('funnel.plans');

  // dev + staging: the backend divides plan prices by 100 (CommissionService
  // .amount_divisor), so show that divided amount to match the real checkout.
  readonly paymentTestMode = environment.paymentTestMode === true;

  // The membership slots to render, club first — it is the one membership on sale.
  //
  // A slot is only shown when it is worth showing: its category still has an
  // active plan, or the member actually holds/owes something in it. That hides the
  // protection slot now that Shield/Executive/Legacy are deactivated — including
  // for members who carry a leftover protection row from the retired Health Finder
  // funnel, whose card would otherwise invite them to buy a product that no longer
  // exists (and whose checkout the backend would refuse). Reactivating those
  // products brings the slot back on its own, with no code change here.
  slots = computed<MembershipSlot[]>(() =>
    ([
      { category: 'club',       app: this.memberships()?.club ?? null },
      { category: 'protection', app: this.memberships()?.protection ?? null }
    ] as MembershipSlot[]).filter(slot => this.slotVisible(slot))
  );

  /** True when this category still has a purchasable plan on the backend. */
  private categoryOnSale(category: MembershipCategory): boolean {
    return this.allPlans().some(p => p.category === category);
  }

  /**
   * Whether a slot earns a dashboard card. Beyond "still on sale" there are two
   * cases a retired category must keep rendering, or we would hide something the
   * member needs: a membership they already bought (its status, documents and
   * cancellation button live on that card), and an order the member cannot simply
   * walk away from — a bank transfer awaiting booking, a gateway mid-flow, a web3
   * transfer already on chain (`blocks_method_change`, the backend's own
   * "not discardable" rule). Deactivating a product does not cancel those.
   *
   * A merely STARTED order (PayPal / ewallet opened and abandoned — the leftovers
   * of the retired Health Finder funnel) is not such a case: the backend would
   * replace it on the next checkout anyway, and a checkout of a deactivated
   * protection plan is refused — so its "finish your application" card was a
   * dead end inviting the member to buy a product that no longer exists.
   */
  private slotVisible(slot: MembershipSlot): boolean {
    if (this.categoryOnSale(slot.category)) return true;
    if (this.slotState(slot.app) === 'purchased') return true;
    return !!slot.app?.pending_order?.blocks_method_change;
  }

  // True while the customer holds nothing yet (no pending or purchased membership
  // in either slot). Drives the "choose one of all five plans" view. Once a plan
  // is selected (an application exists), the dashboard splits into the two slot
  // cards, the empty one inviting the second membership.
  hasMembership = computed(() =>
    this.slots().some(s => this.slotState(s.app) !== 'empty')
  );

  // Admin fee status
  adminFeePaidUntil = signal<Date | null>(null);
  adminFeeExpiringSoon = signal(false);
  adminFeeExpired = signal(false);
  showAdminFeeStatus = signal(false);

  // Presale countdown (ends Jan 31, 2026 23:59 EST)
  presaleEndDate = new Date('2026-01-31T23:59:00-05:00');
  countdownDays = signal(0);
  countdownHours = signal(0);
  countdownMinutes = signal(0);
  countdownSeconds = signal(0);
  presaleEnded = signal(false);

  currentLang = signal<string>('en');

  // PDF modal state
  pdfModalVisible = signal(false);
  pdfModalTitle = signal('');
  pdfUrl = signal<string | null>(null);

  constructor() {
    // Language sync
    effect(() => {
      this.currentLang.set(this.i18n.getLanguage());
    });

    // Load all data
    this.loadUser();
    this.loadDashboardStats();
    this.loadUserPools();
    this.loadUserCards();
    this.loadLatestInfoMedia();
    this.loadDomainInfo();
    this.loadMemberships();

    // Countdown
    effect((onCleanup) => {
      this.updateCountdown();
      const interval = setInterval(() => this.updateCountdown(), 1000);
      onCleanup(() => clearInterval(interval));
    });
  }

  // ────────────────────────────────────────────────
  // Data loading methods
  // ────────────────────────────────────────────────
  private loadUser(): void {
    this.authService.refreshUser()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(user => {
          this.user.set(user);
          if (user && user.level >= 2) { // safe check
            this.checkAdminFeeStatus(user);
          } else {
            this.showAdminFeeStatus.set(false);
          }
        });
  }

  private checkAdminFeeStatus(user: User): void {
    if (!user.admin_fee_paid_until || user.level <= 2) {
      this.showAdminFeeStatus.set(false);
      return;
    }

    this.showAdminFeeStatus.set(true);
    const paidUntil = new Date(user.admin_fee_paid_until);
    this.adminFeePaidUntil.set(paidUntil);

    const now = new Date();
    const daysLeft = Math.ceil((paidUntil.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    this.adminFeeExpired.set(daysLeft < 0);
    this.adminFeeExpiringSoon.set(daysLeft > 0 && daysLeft <= 10);
  }

  private loadDashboardStats(): void {
    this.loading.set(true);
    this.error.set(null);
    this.userService.getDashboardStats()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: data => {
            this.stats.set(data);
            this.loading.set(false);
          },
          error: err => {
            this.logger.error('Dashboard stats error:', err);
            this.error.set(err.error?.error || 'Failed to load dashboard statistics');
            this.loading.set(false);
          }
        });
  }

  private loadUserPools(): void {
    this.poolsLoading.set(true);
    this.userPoolService.getMyPools()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this.userPools.set(response.user_pools.sort((a, b) => b.pool_id - a.pool_id));
            this.confirmedEmailsCount.set(response.confirmed_emails_count);
            this.poolsRequiringEmails.set(response.pools_requiring_emails);
            this.requiredEmailsCount.set(response.required_emails_count);
            this.poolsLoading.set(false);
          },
          error: err => {
            this.logger.error('User pools error:', err);
            this.poolsLoading.set(false);
          }
        });
  }

  private loadUserCards(): void {
    this.cardsLoading.set(true);
    this.cardAccountService.getMyCardAccounts()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this.userCards.set(response.card_accounts);
            this.cardsLoading.set(false);
          },
          error: err => {
            this.logger.error('User cards error:', err);
            this.cardsLoading.set(false);
          }
        });
  }

  private loadLatestInfoMedia(): void {
    this.mediaService
      .loadMedia(this.i18n.getLanguage(), 'info', '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(items => {
        if (!items?.length) {
          this.media.set(null);
          return;
        }

        const latest = items.reduce((prev, curr) => {
          const prevTime = prev.timeUpdated ?? prev.timeCreated ?? 0;
          const currTime = curr.timeUpdated ?? curr.timeCreated ?? 0;
          return currTime > prevTime ? curr : prev;
        });

        this.media.set(latest);
      });
  }

  private loadDomainInfo(): void {
    this.http.get<{ domain_id: number; name: string; lic_1_active: boolean }>('/auth/domain_info')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.domainId.set(res.domain_id);
          this.lic1Active.set(res.lic_1_active);
        },
        error: err => {
          this.logger.error('Domain info error:', err);
          this.domainId.set(null);
          this.lic1Active.set(false);
        }
      });
  }

  // Loads both Global Health membership slots. The template renders one card per
  // slot: "finalize" for PENDING_APPLICATION, the post-purchase status card (Step
  // 15) for a purchased one, or the "add" prompt when the slot is empty/ended.
  private loadMemberships(): void {
    // The SPECIAL is requested by code so its server price comes back in `plans`
    // (customer instruction 2026-10-19: every member may buy it from here).
    this.funnel.getMemberships(SPECIAL_CODE)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: res => {
            this.memberships.set(res.memberships);
            this.contactEmail.set(res.contact_email ?? null);
            const plans = res.plans ?? [];
            this.specialPlan.set(plans.find(p => p.code === SPECIAL_CODE) ?? null);
            this.allPlans.set(plans.filter(p => !p.bundle && !ALL_BUNDLE_PLAN_CODES.includes(p.code)));
          },
          error: () => {
            this.memberships.set(null);
            this.allPlans.set([]);
            this.specialPlan.set(null);
          }
        });
  }

  // The picker's plan cards grouped by tier, club first: the plain WolfPack
  // Membership, then the Practitioner Network (which also gets its own intro and
  // the Founding Practitioner note). Protection plans, being a different slot
  // rather than a different tier, come along in the club group only if the backend
  // ever reports them without a tier — they are deactivated today.
  planTierGroups = computed<PlanTierGroup[]>(() =>
    (['club', 'practitioner'] as MembershipTier[])
      .map(tier => ({
        tier,
        product: (tier === 'practitioner' ? 'practitioner' : 'membership') as ProductKey,
        plans: this.allPlans().filter(p => this.planTier(p) === tier && p.category === 'club' && !p.bundle)
      }))
      .filter(group => group.plans.length > 0)
  );

  /** Open the wizard on a product; its plan step offers the duration options. */
  orderProduct(group: PlanTierGroup): void {
    this.router.navigate(
      ['/', this.currentLang(), 'membership', 'apply'],
      { queryParams: { category: 'club', tier: group.tier } }
    );
  }

  // ----- E-mail confirmation deferred by the direct checkout -----------------

  verifyBannerDismissed = signal<boolean>(this.readVerifyDismissed());
  verifyMailState = signal<'idle' | 'sending' | 'sent' | 'already_confirmed' | 'error'>('idle');

  /** True while the account's e-mail confirmation is still outstanding. */
  showVerifyBanner = computed(() =>
    this.user()?.email_verification_deferred === true && !this.verifyBannerDismissed()
  );

  private readVerifyDismissed(): boolean {
    try { return sessionStorage.getItem(VERIFY_BANNER_DISMISSED_KEY) === '1'; } catch { return false; }
  }

  dismissVerifyBanner(): void {
    this.verifyBannerDismissed.set(true);
    try { sessionStorage.setItem(VERIFY_BANNER_DISMISSED_KEY, '1'); } catch { /* ignore */ }
  }

  resendVerifyMail(): void {
    if (this.verifyMailState() === 'sending') return;
    this.verifyMailState.set('sending');
    this.funnel.resendAccountMail()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.verifyMailState.set(res.status === 'already_confirmed' ? 'already_confirmed' : 'sent');
          if (res.status === 'already_confirmed') this.loadUser();
        },
        error: () => this.verifyMailState.set('error')
      });
  }

  /** Tier headings are only worth showing once there is more than one tier. */
  showTierGroups = computed(() => this.planTierGroups().length > 1);

  /** Maps a plan code to its `funnel.plans.<key>` i18n node. */
  planKey(code: string): string {
    return PLAN_KEY_BY_CODE[code] ?? code.toLowerCase();
  }

  /** A plan's tier — from the backend, with the code list as the fallback. */
  planTier(plan: QuizPlan): MembershipTier {
    return plan.tier ?? (PRACTITIONER_PLAN_CODES.includes(plan.code) ? 'practitioner' : 'club');
  }

  /**
   * Which tier a held membership is on. The backend reports it (it also covers the
   * admin-fee conversions that have no `selected_plan` at all); the plan code is
   * only the fallback.
   */
  tierOf(app: MembershipApplication | null): MembershipTier {
    if (app?.tier) return app.tier;
    const code = app?.selected_plan ?? app?.recommended_plan ?? '';
    return PRACTITIONER_PLAN_CODES.includes(code) ? 'practitioner' : 'club';
  }

  /** The tier label shown next to the plan name on a purchased card. */
  tierLabel(app: MembershipApplication | null): string {
    return this.translate.translate(`membership.tier.${this.tierOf(app)}`);
  }

  /** "Renew" wording — the Practitioner Network renews as itself, not as a club. */
  renewCtaKey(app: MembershipApplication | null): string {
    return this.tierOf(app) === 'practitioner'
      ? 'membership.renew.cta_practitioner'
      : 'membership.renew.cta';
  }

  /** Feature bullet list for a plan card (from the `funnel.plans` locale node). */
  planFeatures(code: string): string[] {
    const c = this.planContent() as Record<string, any> | undefined;
    return c?.[this.planKey(code)]?.features ?? [];
  }

  /** A single `funnel.plans.<key>.<field>` string (tagline / price_year / …). */
  planField(code: string, field: string): string {
    const c = this.planContent() as Record<string, any> | undefined;
    return c?.[this.planKey(code)]?.[field] ?? '';
  }

  /** Start buying a specific plan from the picker, carrying its slot + code. */
  orderPlan(plan: QuizPlan): void {
    this.router.navigate(
      ['/', this.currentLang(), 'membership', 'apply'],
      { queryParams: { category: plan.category, plan: plan.code } }
    );
  }

  /** State of a slot's card: unfinished, purchased, or empty (nothing/ended). */
  slotState(app: MembershipApplication | null): 'pending' | 'purchased' | 'expired' | 'empty' {
    const s = app?.membership_status;
    if (s === 'PENDING_APPLICATION') return 'pending';
    if (s === 'PENDING_CANCELLATION_PERIOD' || s === 'ACTIVE') return 'purchased';
    // An expired membership must NOT fall back to 'empty': the member still owns
    // this slot, and renewing revives the very same membership rather than starting
    // a new one. Showing the plan picker instead would hide that.
    if (s === 'EXPIRED') return 'expired';
    return 'empty';
  }

  /**
   * The outstanding bank transfer for a slot's pending application (order started
   * but not yet booked), or null — drives the "complete your bank transfer" card.
   */
  pendingBankTransferFor(app: MembershipApplication | null) {
    const po = app?.pending_order;
    return po && po.payment_method === 'bank_transfer' && po.status !== 'completed' ? po : null;
  }

  /** True when the slot previously held a membership that ended. */
  hadPreviousMembership(app: MembershipApplication | null): boolean {
    const s = app?.membership_status;
    return s === 'CANCELED' || s === 'DECLINED';
  }

  /** "WolfPack Global Health Membership — 12 weeks" for a slot's chosen (or
   *  recommended) plan — the buyer-facing name, never the internal product name. */
  planName(app: MembershipApplication | null): string {
    const code = app?.selected_plan ?? app?.recommended_plan;
    if (!code) return '';
    return productLabel(this.translate, code, app?.term_weeks ?? null, app?.product_key);
  }

  /** Start buying the given slot (empty-slot "add" / "buy" CTA). */
  addMembership(category: MembershipCategory): void {
    this.router.navigate(
      ['/', this.currentLang(), 'membership', 'apply'],
      { queryParams: { category } }
    );
  }

  /**
   * The plan a tier's CTA opens the wizard on — the first one the backend lists for
   * that tier, so the order of the offer lives in one place. Null when the tier has
   * nothing on sale, which is what hides its CTA.
   */
  tierPlan(category: MembershipCategory, tier: MembershipTier): QuizPlan | null {
    return this.allPlans().find(p => p.category === category && this.planTier(p) === tier) ?? null;
  }

  /** True when this tier has something to sell in this slot (gates its CTA). */
  tierOnSale(category: MembershipCategory, tier: MembershipTier): boolean {
    return !!this.tierPlan(category, tier);
  }

  /** Buy a specific tier of a slot — the wizard's plan step still offers the other. */
  addMembershipTier(category: MembershipCategory, tier: MembershipTier): void {
    // `tier`, not `plan`: the wizard then opens on that product's duration options
    // instead of skipping the plan step with one rhythm preselected.
    this.router.navigate(
      ['/', this.currentLang(), 'membership', 'apply'],
      { queryParams: { category, tier } }
    );
  }

  /**
   * Which "buy this membership" cards an empty slot shows. The club slot sells two
   * tiers, so it offers both (each with its own copy and CTA); every other slot
   * keeps its single category card. A tier with nothing active on the backend is
   * dropped — and if that leaves the club slot with nothing, its own card stays so
   * the member is not left with a blank slot.
   */
  buyWidgetVariants(slot: MembershipSlot): string[] {
    if (slot.category !== 'club') return [slot.category];
    const tiers: string[] = (['club', 'practitioner'] as MembershipTier[])
      .filter(tier => this.tierOnSale('club', tier));
    const variants = tiers.length ? tiers : ['club'];
    // The DNA & Health SPECIAL is open to everyone, a new member included — as a
    // third card whenever the backend lists it as on sale.
    return this.specialPlan() ? [...variants, 'dna_health_special'] : variants;
  }

  /** The body copy of a buy-widget card: "again" wording for the tier they had. */
  buyWidgetBodyKey(slot: MembershipSlot, variant: string): string {
    if (this.hadPreviousMembership(slot.app) && variant === this.tierOf(slot.app)) {
      return 'membership.buy_widget.body_again';
    }
    return `membership.buy_widget.${variant}_body`;
  }

  /** Buy-widget CTA: a tier of the club slot, or the slot itself. */
  startBuyWidget(category: MembershipCategory, variant: string): void {
    if (variant === 'dna_health_special') {
      this.startSpecialPurchase();
      return;
    }
    if (variant === 'club' || variant === 'practitioner') {
      this.addMembershipTier(category, variant);
      return;
    }
    this.addMembership(category);
  }

  /** The server's Practitioner Network upgrade offer for a slot, or null. */
  upgradeOfferFor(app: MembershipApplication | null): UpgradeOffer | null {
    return app?.upgrade_offer ?? null;
  }

  /** The server's DNA & Health SPECIAL upgrade offer ($2,099, decision D), or null. */
  specialUpgradeOfferFor(app: MembershipApplication | null): SpecialUpgradeOffer | null {
    return app?.special_upgrade_offer ?? null;
  }

  // ----- Regular DNA & Health SPECIAL purchase (customer instruction 2026-10-19) --

  /**
   * True when this slot gets the regular SPECIAL card: the server offers the
   * SPECIAL to this membership (`offered_bundle_codes`) and it is not already on a
   * bundle. The server's set_plan/checkout enforce the same rule.
   */
  specialPurchaseAvailable(app: MembershipApplication | null): boolean {
    if (!app || ALL_BUNDLE_PLAN_CODES.includes(app.selected_plan ?? '')) return false;
    return (app.offered_bundle_codes ?? []).includes(SPECIAL_CODE);
  }

  /** The SPECIAL's price, formatted. Always the server's plan price when listed. */
  specialPrice(): string {
    const plan = this.specialPlan();
    // Display-only fallback for a backend that did not list the plan: the checkout
    // always charges the server's own Product price, never this number.
    return this.offerAmount(plan?.price ?? PRODUCT_PRICES.dna_health_special.once, 'USD');
  }

  /** The five components of the SPECIAL, in the customer's order. */
  readonly specialComponents = ['dna', 'nutrition', 'fitness', 'dr_future', 'membership'];

  /** Practitioner tier: the SPECIAL does not end the paid Practitioner term. */
  isPractitionerTier(app: MembershipApplication | null): boolean {
    return app?.tier === 'practitioner';
  }

  /** Opens the wizard in bundle mode on the regular SPECIAL; the server re-checks the offer. */
  startSpecialPurchase(): void {
    this.router.navigate(
      ['/', this.currentLang(), 'membership', 'apply'],
      { queryParams: { category: 'club', plan: SPECIAL_CODE } }
    );
  }

  /** "2,099.00 USDT" — an amount of an offer, in the offer's own currency. */
  offerAmount(value: number | string | null | undefined, currency?: string | null): string {
    const amount = new Intl.NumberFormat(this.currentLang(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(value ?? 0));
    return `${amount} ${currency || 'USD'}`;
  }

  /** Opens the wizard on the Practitioner-Membership-on-SPECIAL step (decision 3 of
   *  2026-10-04); the offer is re-read there. */
  startPractitionerRenewal(): void {
    this.router.navigate(
      ['/', this.currentLang(), 'membership', 'apply'],
      { queryParams: { category: 'club', practitioner_renewal: '1' } }
    );
  }

  // ----- Special conditions of a manual booking (decision 7 of 2026-10-04) ----
  /** The booking whose confirmation is being sent, or null. */
  termsBusy  = signal<number | null>(null);
  termsError = signal<string | null>(null);

  /** The member's manual bookings whose special conditions still wait for confirmation. */
  openBookingTerms(app: MembershipApplication | null): BookingTerms[] {
    return (app?.booking_terms ?? []).filter(t => t.required && !t.acknowledged);
  }

  /** The exact conditions of THIS booking, one line each — every value is the server's. */
  bookingTermsLines(t: BookingTerms): string[] {
    const lines: string[] = [];
    const product = this.translate.translate(`membership.booking_terms.product.${t.product_code}`);
    lines.push(this.translate.translate('membership.booking_terms.product_line', {
      product: product.startsWith('membership.') ? t.product_code : product
    }));
    if (t.discounted) {
      lines.push(this.translate.translate('membership.booking_terms.price_line', {
        charged: this.offerAmount(t.charged_price, t.currency), regular: this.offerAmount(t.regular_price, t.currency)
      }));
    }
    if (t.guarantee_mode === 'none') {
      lines.push(this.translate.translate('membership.booking_terms.guarantee_none', {
        standard: this.offerAmount(t.standard_guarantee_amount, 'USD')
      }));
    } else if (t.guarantee_mode === 'custom') {
      lines.push(this.translate.translate('membership.booking_terms.guarantee_custom', {
        amount: this.offerAmount(t.guarantee_amount, 'USD'), standard: this.offerAmount(t.standard_guarantee_amount, 'USD')
      }));
    } else if (t.guarantee_mode === 'standard') {
      lines.push(this.translate.translate('membership.booking_terms.guarantee_standard', {
        amount: this.offerAmount(t.standard_guarantee_amount, 'USD')
      }));
    }
    // An admin booked an immediate start: confirming it is the member's own
    // declaration — the only thing that lets the DNA kit ship inside the 14 days
    // (customer rule 2026-10-04). Without it the kit waits for day 15.
    if (t.immediate_start) {
      lines.push(this.translate.translate('membership.booking_terms.immediate_start'));
    }
    return lines;
  }

  acknowledgeBookingTerms(t: BookingTerms): void {
    this.termsBusy.set(t.booking_id);
    this.termsError.set(null);
    this.funnel.acknowledgeBookingTerms(t.booking_id, t.terms_version)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => { this.termsBusy.set(null); this.loadMemberships(); },
        error: err => {
          this.termsBusy.set(null);
          this.termsError.set(err?.error?.error || this.translate.translate('membership.booking_terms.error'));
          // A changed version (409) comes back with the current conditions: reload.
          this.loadMemberships();
        }
      });
  }

  /** Opens the wizard on the ONE SPECIAL-upgrade card; the offer is re-read there. */
  startSpecialUpgrade(): void {
    this.router.navigate(
      ['/', this.currentLang(), 'membership', 'apply'],
      { queryParams: { category: 'club', plan: 'GH_DNA_HEALTH_SPECIAL_UPGRADE' } }
    );
  }

  /** The upgrade's total, in the offer's own currency (never recomputed here). */
  upgradePrice(offer: UpgradeOffer): string {
    const amount = new Intl.NumberFormat(this.currentLang(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(offer.total_price));
    return `${amount} ${offer.currency || 'USD'}`;
  }

  /**
   * Open the wizard's upgrade step. No plan travels with it: the package and the
   * price are the server's offer, and the wizard re-reads it rather than trusting
   * anything this page put in the URL.
   */
  startUpgrade(app: MembershipApplication | null): void {
    this.router.navigate(
      ['/', this.currentLang(), 'membership', 'apply'],
      { queryParams: { category: app?.plan_category ?? 'club', upgrade: 1 } }
    );
  }

  /**
   * True when this membership's route into the Practitioner Network is the REGULAR
   * price: it was never paid for (an admin-fee conversion, a $0 gift) or it was paid
   * on a retired rhythm, so the difference-price upgrade does not apply. The server
   * decides — the two routes are mutually exclusive there, and this page must not
   * second-guess which one a member is on.
   */
  practitionerJoinAvailable(app: MembershipApplication | null): boolean {
    return app?.practitioner_join_available === true;
  }

  /** The date the member's purchase would be added to (their current coverage end). */
  practitionerJoinUntil(app: MembershipApplication | null): string {
    const until = app?.paid_through ?? app?.coverage_end_date;
    return until ? this.formatDate(until) : '';
  }

  /**
   * The upgrade box's text, one entry per paragraph. The copy opens with the
   * eligibility note (health professionals only), the same wording the
   * full-price "join" box carries, before the price mechanics.
   */
  upgradeParagraphs(): string[] {
    const text = this.translate.translate('membership.upgrade.dashboard_body');
    return (text || '').split(/\n\s*\n/).map(s => s.trim()).filter(Boolean);
  }

  /**
   * The join text is one translation with blank-line separated paragraphs (the
   * "professionals only" notice followed by how the purchased term stacks on the
   * current end date); rendered as real paragraphs rather than one pre-line block.
   */
  practitionerJoinParagraphs(app: MembershipApplication | null): string[] {
    const text = this.translate.translate('membership.join_practitioner.dashboard_body', {
      until: this.practitionerJoinUntil(app),
    });
    return (text || '').split(/\n\s*\n/).map(s => s.trim()).filter(Boolean);
  }

  /**
   * Open the wizard on the Practitioner Network. The plan is the first one the
   * BACKEND offers for that tier, so the rhythm this CTA lands on follows the offer
   * rather than a code literal here; without one the wizard's plan step still shows
   * the choice.
   */
  startPractitionerJoin(app: MembershipApplication | null): void {
    // The wizard opens on the Practitioner Membership's duration options (the
    // backend's `offered_plan_codes` decides which are shown).
    this.router.navigate(
      ['/', this.currentLang(), 'membership', 'apply'],
      { queryParams: { category: app?.plan_category ?? 'club', tier: 'practitioner' } }
    );
  }

  /**
   * Renew a lapsed membership. Deliberately WITHOUT a plan param: coverage has
   * ended, so every offered plan is open again (both rhythms of both tiers) and
   * pre-selecting the old one would hide the choice.
   */
  renewWithoutPlan(app: MembershipApplication | null): void {
    this.router.navigate(
      ['/', this.currentLang(), 'membership', 'apply'],
      { queryParams: { category: app?.plan_category ?? 'club' } }
    );
  }

  /**
   * Extend an existing membership. Renewal is allowed at any time — while coverage
   * is still running (the new term stacks on the current end, so nothing paid for
   * is lost) and after it lapsed. The wizard picks up the existing membership on
   * its own, so this is the same navigation as resuming.
   */
  renewMembership(app: MembershipApplication | null): void {
    // A DNA & Health bundle is renewed as the membership it contains — the yearly
    // rhythm — not by re-buying the kit and the plan (Website V2). The wizard's plan
    // step still offers the weekly rhythm too.
    if (app && ALL_BUNDLE_PLAN_CODES.includes(app.selected_plan ?? '')) {
      this.router.navigate(
        ['/', this.currentLang(), 'membership', 'apply'],
        { queryParams: { category: app.plan_category ?? 'club', plan: 'GH_CLUB_YEARLY' } }
      );
      return;
    }
    this.resumeMembership(app);
  }

  // ----- DNA & Health bundle fulfilment (Website V2) -------------------------

  /** Guarantee claim in flight / just submitted, per membership id. */
  // ----- My referral links (Website V2) ------------------------------------------

  /**
   * The website's origin, taken from the user's `referral_url_wph`
   * (`<base>/r/<id>`, built by the backend from REFERRAL_BASE_URL_WPH) so staging
   * and production each get their own host; the current origin when it is absent.
   */
  /**
   * Base of the public website the links point at. The dashboard is served from
   * the website's own host, so the current origin IS the right site — production
   * on production, the staging host on staging (the customer tests the links
   * there; a production link would leave the staging system and attribute nothing).
   * Only the local dev server (localhost, where `/` is the app and not the
   * landing) falls back to the backend's referral URL.
   */
  private referralBase = computed(() => {
    const host = window.location.hostname;
    const local = host === 'localhost' || host === '127.0.0.1';
    const wph = this.user()?.referral_url_wph?.trim();
    const base = !local || !wph ? window.location.origin : wph.replace(/\/r\/\d+\/?$/, '');
    return base.replace(/\/+$/, '');
  });

  /** One link per website journey / bundle: `<base><path>?ref=<user id>`. */
  referralLinks = computed<ReferralLink[]>(() => {
    const id = this.user()?.id;
    if (!id) return [];
    const base = this.referralBase();
    return REFERRAL_TARGETS.map(t => ({
      key: t.key,
      url: `${base}${t.path}?ref=${id}${t.hash ? '#' + t.hash : ''}`
    }));
  });

  /** Key of the link just copied — drives the short "copied" feedback. */
  copiedReferral = signal<string | null>(null);
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  /** Copies a referral link (Clipboard API, textarea fallback — the same pattern as
   *  credo's ReferralLinkComponent). */
  copyReferral(link: ReferralLink): void {
    const done = () => {
      this.copiedReferral.set(link.key);
      if (this.copiedTimer) clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => this.copiedReferral.set(null), 2000);
    };
    const fallback = () => {
      const textarea = document.createElement('textarea');
      textarea.value = link.url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      try {
        textarea.select();
        if (document.execCommand('copy')) done();
      } catch (err) {
        this.logger.error('Referral link copy failed:', err);
      } finally {
        document.body.removeChild(textarea);
      }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link.url).then(done).catch(fallback);
    } else {
      fallback();
    }
  }

  guaranteeBusy = signal(false);
  guaranteeError = signal<string | null>(null);

  // ----- Start area of the SPECIAL (customer briefing 2026-11-02) -------------
  //
  // The member's first task after the purchase is the SHIPPING ADDRESS of the
  // DNA kit (§3: worldwide, the country is one field of the address, checked for
  // completeness before the lab order). The Practitioner team's mailbox (§4)
  // comes from the server per brand. The step list below puts the fulfilment
  // statuses into plain words.

  /** The Practitioner team's e-mail (server-configured; null = none shown). */
  contactEmail = signal<string | null>(null);
  /** Membership id whose shipping-address form is open. */
  shippingFormFor = signal<number | null>(null);
  shippingBusy = signal(false);
  shippingError = signal<string | null>(null);
  shippingInvalidFields = signal<string[]>([]);
  shipping: BundleShippingAddress = MainPageComponent.emptyShipping();
  /** Every country, named in the UI language (no delivery restriction — worldwide). */
  countries = computed(() => countryOptions(this.currentLang()));

  private static emptyShipping(): BundleShippingAddress {
    return { shipping_name: '', shipping_address: '', shipping_address2: '', shipping_city: '',
             shipping_postcode: '', shipping_state: '', shipping_country: '', shipping_phone: '' };
  }

  openShippingForm(app: MembershipApplication | null): void {
    const bundle = this.bundleOf(app);
    if (!app || !bundle?.shipping_address_editable) return;
    const current = bundle.shipping_address;
    const user = this.user();
    // Prefill: the address already entered, else the member's name and whatever
    // the profile knows (direct buyers usually have nothing there).
    this.shipping = {
      ...MainPageComponent.emptyShipping(),
      shipping_name: current?.shipping_name ?? [user?.first_name, user?.last_name].filter(Boolean).join(' '),
      shipping_address: current?.shipping_address ?? '',
      shipping_address2: current?.shipping_address2 ?? '',
      shipping_city: current?.shipping_city ?? '',
      shipping_postcode: current?.shipping_postcode ?? '',
      shipping_state: current?.shipping_state ?? '',
      shipping_country: current?.shipping_country ?? '',
      shipping_phone: current?.shipping_phone ?? ''
    };
    this.shippingError.set(null);
    this.shippingInvalidFields.set([]);
    this.shippingFormFor.set(app.id);
  }

  cancelShippingForm(): void {
    this.shippingFormFor.set(null);
    this.shippingError.set(null);
  }

  /** Name, street, city and country are required — the same rule as the server's. */
  canSubmitShipping(): boolean {
    const a = this.shipping;
    return !!(a.shipping_name?.trim() && a.shipping_address?.trim() && a.shipping_city?.trim() &&
              a.shipping_country?.trim()) && !this.shippingBusy();
  }

  shippingFieldInvalid(field: string): boolean {
    return this.shippingInvalidFields().includes(field);
  }

  submitShippingAddress(app: MembershipApplication | null): void {
    if (!app || !this.canSubmitShipping()) return;
    this.shippingBusy.set(true);
    this.shippingError.set(null);
    this.shippingInvalidFields.set([]);
    const trim = (value: string | null | undefined) => (value ?? '').trim();
    const a = this.shipping;
    const address: BundleShippingAddress = {
      shipping_name: trim(a.shipping_name), shipping_address: trim(a.shipping_address),
      shipping_address2: trim(a.shipping_address2), shipping_city: trim(a.shipping_city),
      shipping_postcode: trim(a.shipping_postcode), shipping_state: trim(a.shipping_state),
      shipping_country: trim(a.shipping_country).toUpperCase(), shipping_phone: trim(a.shipping_phone)
    };
    this.funnel.updateBundleShippingAddress(address, app.order_id ?? null)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.shippingBusy.set(false);
            this.shippingFormFor.set(null);
            this.loadMemberships();
          },
          error: err => {
            this.shippingBusy.set(false);
            const code = err?.error?.code;
            if (code === 'shipping_address_invalid') {
              this.shippingInvalidFields.set(err?.error?.fields ?? []);
              this.shippingError.set(this.translate.translate('membership.bundle.shipping.error_invalid'));
            } else if (code === 'shipping_address_locked' || err?.status === 409) {
              this.shippingError.set(this.translate.translate('membership.bundle.shipping.error_locked'));
              this.loadMemberships();
            } else {
              this.shippingError.set(this.translate.translate('membership.bundle.shipping.error'));
            }
          }
        });
  }

  /** The stored address as postal lines, country named in the UI language. */
  shippingAddressLines(bundle: BundleFulfillment): string[] {
    const a = bundle.shipping_address;
    if (!a) return [];
    return [
      a.shipping_name,
      a.shipping_address,
      a.shipping_address2,
      [a.shipping_postcode, a.shipping_city].filter(Boolean).join(' '),
      a.shipping_state,
      countryName(a.shipping_country, this.currentLang()),
      a.shipping_phone
    ].filter((line): line is string => !!line && line.trim().length > 0);
  }

  /**
   * The SPECIAL's course in plain words (customer briefing 2026-11-02,
   * "verständlicher Ablauf"): payment → address → shipping release → kit on its
   * way → sample in the lab → analysis → plans. Each step is done, current or
   * upcoming, derived from the fulfilment statuses — nothing is stored for it.
   */
  bundleSteps(bundle: BundleFulfillment): { key: string; state: 'done' | 'current' | 'upcoming'; date?: string | null }[] {
    const kitOrder = ['pending', 'ready_for_partner', 'kit_shipped', 'kit_delivered', 'sample_received',
                      'analysis_in_progress', 'analysis_complete'];
    const kitIndex = Math.max(0, kitOrder.indexOf(bundle.dna_kit_status));
    const shipped = kitIndex >= kitOrder.indexOf('kit_shipped');
    const released = !!bundle.ship_after && new Date(bundle.ship_after).getTime() <= Date.now();
    const addressDone = !!bundle.shipping_address?.complete;
    const plansDone = bundle.nutrition_plan_status === 'delivered' && bundle.fitness_plan_status === 'delivered';
    const flags: { key: string; done: boolean; date?: string | null }[] = [
      { key: 'paid',     done: true },
      { key: 'address',  done: addressDone },
      { key: 'release',  done: released || shipped, date: bundle.ship_after },
      { key: 'shipped',  done: shipped, date: bundle.shipped_at },
      { key: 'sample',   done: kitIndex >= kitOrder.indexOf('sample_received') },
      { key: 'analysis', done: kitIndex >= kitOrder.indexOf('analysis_complete') },
      { key: 'plans',    done: plansDone }
    ];
    let currentSeen = false;
    return flags.map(f => {
      let state: 'done' | 'current' | 'upcoming' = 'done';
      if (!f.done) { state = currentSeen ? 'upcoming' : 'current'; currentSeen = true; }
      return { key: f.key, state, date: f.date };
    });
  }

  /** The fulfilment line of a membership bought as a bundle, or null. */
  bundleOf(app: MembershipApplication | null): BundleFulfillment | null {
    return app?.bundle ?? null;
  }

  bundleKitLabel(bundle: BundleFulfillment): string {
    return this.translate.translate(`membership.bundle.kit_status.${bundle.dna_kit_status}`) || bundle.dna_kit_status;
  }

  /** "available" / "in preparation" for the nutrition or the fitness plan. */
  bundlePlanLabel(status: string | null | undefined): string {
    const value = status || 'pending';
    return this.translate.translate(`membership.bundle.plan_status.${value}`) || value;
  }

  /** The goal the member chose on the website: the journey's name, plus the goal
   *  itself when it is more specific than the journey. '' when none was chosen. */
  bundleGoalLabel(bundle: BundleFulfillment): string {
    const parts: string[] = [];
    const journeyKey = bundle.customer_journey ? `membership.bundle.journey.${bundle.customer_journey}` : null;
    if (journeyKey) {
      const label = this.translate.translate(journeyKey);
      parts.push(label && label !== journeyKey ? label : this.humanize(bundle.customer_journey!));
    }
    const goal = bundle.customer_goal;
    if (goal && goal !== bundle.customer_journey) {
      const key = `membership.bundle.goal.${goal}`;
      const label = this.translate.translate(key);
      parts.push(label && label !== key ? label : this.humanize(goal));
    }
    return parts.join(' · ');
  }

  private humanize(slug: string): string {
    const text = slug.replace(/_/g, ' ');
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  /** Membership id whose claim form is open (the claim is a short form, not a click). */
  guaranteeFormFor = signal<number | null>(null);
  guaranteeMethod  = signal<GuaranteePayoutMethod | null>(null);
  guaranteeDetails = signal('');
  guaranteeConfirmed = signal(false);

  /** A BSC address for a crypto payout (mirrors the backend's check). */
  private static readonly BSC_ADDRESS = /^0x[0-9a-fA-F]{40}$/;

  guaranteeDetailsValid(): boolean {
    const details = this.guaranteeDetails().trim();
    if (!details || details.length > 1000) return false;
    return this.guaranteeMethod() !== 'crypto' || MainPageComponent.BSC_ADDRESS.test(details);
  }

  canSubmitGuarantee(): boolean {
    return !!this.guaranteeMethod() && this.guaranteeDetailsValid() && this.guaranteeConfirmed() &&
           !this.guaranteeBusy();
  }

  openGuaranteeForm(app: MembershipApplication | null): void {
    if (!app || !this.bundleOf(app)?.guarantee_claim_open) return;
    this.guaranteeFormFor.set(app.id);
    this.guaranteeMethod.set(null);
    this.guaranteeDetails.set('');
    this.guaranteeConfirmed.set(false);
    this.guaranteeError.set(null);
  }

  closeGuaranteeForm(): void {
    this.guaranteeFormFor.set(null);
  }

  /** "Crypto (USDT/USDC)" / "USD bank transfer" for the submitted line. */
  guaranteePayoutLabel(method: string | null | undefined): string {
    return method ? this.translate.translate(`membership.guarantee.payout.${method}.label`) : '';
  }

  /**
   * Claim the SPECIAL's satisfaction guarantee — once, inside the window the server
   * reports, with the payout the member chose. The form's confirmation checkbox is
   * the confirmation: it cannot be undone from here.
   */
  claimGuarantee(app: MembershipApplication | null): void {
    const bundle = this.bundleOf(app);
    const method = this.guaranteeMethod();
    if (!bundle?.guarantee_claim_open || !method || !this.canSubmitGuarantee()) return;

    this.guaranteeBusy.set(true);
    this.guaranteeError.set(null);
    this.funnel.claimBundleGuarantee(method, this.guaranteeDetails().trim())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.guaranteeBusy.set(false);
            this.guaranteeFormFor.set(null);
            this.loadMemberships();
          },
          error: err => {
            this.guaranteeBusy.set(false);
            const code = err?.error?.code;
            this.guaranteeError.set(
              code ? this.translate.translate(`membership.guarantee.payout.error.${code}`)
                   : this.translate.translate('membership.guarantee.error'));
            // 409: claimed meanwhile / window closed — show the server's state.
            if (err?.status === 409) {
              this.guaranteeFormFor.set(null);
              this.loadMemberships();
            }
          }
        });
  }

  /** Resume an in-progress slot, forwarding its category (+ plan when known). */
  resumeMembership(app: MembershipApplication | null): void {
    if (!app) return;
    const plan = app.selected_plan ?? app.recommended_plan;
    this.router.navigate(
      ['/', this.currentLang(), 'membership', 'apply'],
      { queryParams: { category: app.plan_category ?? 'club', ...(plan ? { plan } : {}) } }
    );
  }

  cancelMembership(app: MembershipApplication | null): void {
    const category = app?.plan_category;
    if (!category) return;
    if (!confirm(this.translate.translate('membership.status.cancel_confirm'))) return;
    this.funnel.cancelMembership(category)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.loadMemberships(),
          error: () => { /* deadline passed / already cancelled — reload to reflect state */ this.loadMemberships(); }
        });
  }

  /** View a generated membership document (opens the PDF in a new tab). */
  viewMembershipDocument(docType: string, category: MembershipCategory): void {
    this.funnel.downloadMembershipDocument(docType, category)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: blob => window.open(URL.createObjectURL(blob), '_blank'),
          error: () => { /* not generated yet */ }
        });
  }

  documentLabel(docType: string): string {
    return this.translate.translate(`membership.status.doc.${docType}`) || docType;
  }

  // ────────────────────────────────────────────────
  // Countdown
  // ────────────────────────────────────────────────
  private updateCountdown(): void {
    const distance = this.presaleEndDate.getTime() - Date.now();
    if (distance < 0) {
      this.presaleEnded.set(true);
      return;
    }

    this.countdownDays.set(Math.floor(distance / (1000 * 60 * 60 * 24)));
    this.countdownHours.set(Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
    this.countdownMinutes.set(Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)));
    this.countdownSeconds.set(Math.floor((distance % (1000 * 60)) / 1000));
  }

  // ────────────────────────────────────────────────
  // PDF & Media
  // ────────────────────────────────────────────────
  openPdfModal(type: 'provisions' | 'license' | 'cards'): void {
    let pdfPath = '';
    let titleKey = '';
    
    if (type === 'provisions') {
      pdfPath = this.currentLang() === 'de'
        ? '/assets/commisison_overview_1.0_de.pdf'
        : '/assets/commisison_overview_1.0_en.pdf';
      titleKey = 'dashboard_pdf_provisions_title';
    } else if (type === 'license') {
      pdfPath = this.currentLang() === 'de'
        ? '/assets/FAC3LESS_Pool-Lizenzsystem_1.3.pdf'
        : '/assets/FAC3LESS_PoolLicenseSystem_1.3_en.pdf';
      titleKey = 'dashboard_pdf_license_title';
    } else if (type === 'cards') {
      pdfPath = this.currentLang() === 'de'
        ? '/assets/card_overview_1.0_de.pdf'
        : '/assets/card_overview_1.0_en.pdf';
      titleKey = 'dashboard_pdf_cards_title';
    }

    if (pdfPath) {
      this.pdfModalTitle.set(this.translate.translate(titleKey));

      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      const pdfLink: MediaLink = { src: `${baseUrl}${pdfPath}`, mimeType: 'application/pdf' };
      const coverLink: ImageLink = {
        src: `${window.location.origin}/assets/images/default-media.jpg`,
        mimeType: 'image/jpg',
        width: 0,
        height: 0
      };

      const media: PdfMedia = {
        id: '0',
        name: 'default',
        type: 'pdf',
        cover: coverLink,
        links: [pdfLink],
        timeUpdated: Date.now(),
        trackReactions: false
      };

      this.openMediaViewer(media);
    }
  }

  openMediaViewer(media: Media): void {
    this.mediaViewer.open({ media }).then(overlayRef => {
      this.router.events.pipe(
        filter(event => event instanceof NavigationStart),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        overlayRef.destroy();
      });
  
      overlayRef.onDestroy.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.logger.debug('Media viewer overlay destroyed');
      });
  
      overlayRef.onAction.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(({ action, parameter }) => {
        if (action === 'played') {
          const playedMedia = parameter as Media;
          if (playedMedia.trackReactions) {
            this.mediaService.setReaction(playedMedia.id, 'played')
              .then(() => this.loadLatestInfoMedia());
          }
        }
      });
    }).catch(err => {
      this.logger.error('Failed to open media viewer:', err);
    });
  }

  // ────────────────────────────────────────────────
  // Formatting & helpers (unchanged)
  // ────────────────────────────────────────────────
  formatCurrency(value: number): string {
    return new Intl.NumberFormat(this.currentLang(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value) + ' USD';
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString(this.currentLang(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  getPercentage(pool: UserPool): number {
    return pool.max_balance === 0 ? 0 : Math.round((pool.turnover / pool.max_balance) * 100);
  }

  hasPoolsWithEmailNotOk(): boolean {
    return this.userPools().some(p => p.needs_email && !p.has_emails);
  }

  getCardsWithoutInitialPayment(): number {
    return this.userCards().filter(c => !c.initialpayment_done).length;
  }

  getCardTypeLabel(cardType: string): string {
    const labels: Record<string, string> = {
      silber: this.translate.translate('dashboard_card_type_silber'),
      gold: this.translate.translate('dashboard_card_type_gold'),
      platin: this.translate.translate('dashboard_card_type_platin'),
      m3: this.translate.translate('dashboard_card_type_m3')
    };
    return labels[cardType] || cardType;
  }

  getCardTypeBadgeColor(cardType: string): string {
    const colors: Record<string, string> = {
      silber: 'secondary',
      gold: 'warning',
      platin: 'info',
      m3: 'primary'
    };
    return colors[cardType] || 'dark';
  }

  getLevelBadgeUrl(level: number): string {
    return `/assets/images/badge${level || 0}.png`;
  }

  getLevelName(level: number): string {
    const names: Record<number, string> = {
      0: this.translate.translate('dashboard_level_0_name'),
      1: this.translate.translate('dashboard_level_1_name'),
      2: this.translate.translate('dashboard_level_2_name'),
      3: this.translate.translate('dashboard_level_3_name'),
      4: this.translate.translate('dashboard_level_4_name'),
      5: this.translate.translate('dashboard_level_5_name'),
      6: this.translate.translate('dashboard_level_6_name'),
      7: this.translate.translate('dashboard_level_7_name', { appName: this.translate.translate('app.name') })
    };
    return names[level] || this.translate.translate('dashboard_level_unknown');
  }

  getLevelDescription(level: number): string {
    const desc: Record<number, string> = {
      0: this.translate.translate('dashboard_level_0_desc'),
      1: this.translate.translate('dashboard_level_1_desc'),
      2: this.translate.translate('dashboard_level_2_desc'),
      3: this.translate.translate('dashboard_level_3_desc'),
      4: this.translate.translate('dashboard_level_4_desc'),
      5: this.translate.translate('dashboard_level_5_desc'),
      6: this.translate.translate('dashboard_level_6_desc'),
      7: this.translate.translate('dashboard_level_7_desc')
    };
    return desc[level] || this.translate.translate('dashboard_level_unknown_desc');
  }

  onBadgeImageError(event: Event): void {
    (event.target as HTMLImageElement).src = '/assets/images/badge0.png';
  }

  formatAdminFeeDate(): string {
    const date = this.adminFeePaidUntil();
    if (!date) return '';
    return date.toLocaleDateString(this.currentLang(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  }
}